import * as Yup from "yup";
import { FlexContext } from "./flex-context";
import { FlexSectionController } from "./flex-section-controller";
import { isSectionEmbedded } from "./utils";

const createSectionNestedValidators = (
	controller: FlexSectionController,
	initialData: unknown,
	translation: (key: string) => string
): { [key: string]: Yup.AnySchema } => {
	return {
		...controller.fieldControllers.reduce(
			(a, field) => ({
				...a,
				[field.schema.attribute.identifier]: field.createValidationRule(initialData, translation),
			}),
			{} as any
		),
		...controller.sectionControllers.reduce(
			(a, section) => ({
				...a,
				...createSectionValidatorsPart(section, initialData, translation),
			}),
			{} as any
		),
	};
};

const getEmbeddedSectionControllers = (controller: FlexSectionController) => {
	const controllers = [...controller.sectionControllers];
	const result = [controller];
	while (controllers.length > 0) {
		const current = controllers.pop()!;
		if (!isSectionEmbedded(current.schema)) {
			continue;
		}

		result.push(current);
		controllers.push(...current.sectionControllers);
	}

	return result;
};

const createSectionValidatorSchema = (
	controller: FlexSectionController,
	initialData: unknown,
	translation: (key: string) => string
): Yup.ObjectSchema<any> | Yup.ArraySchema<any> => {
	const objectSchema = Yup.object().shape(createSectionNestedValidators(controller, initialData, translation)).nullable();
	const nestedEmbeddedControllers = getEmbeddedSectionControllers(controller);
	const extendedSchema: Yup.ObjectSchema<any> = nestedEmbeddedControllers.reduce(
		(a, nestedController) => nestedController.createValidationRule?.(a, initialData, translation) ?? a,
		objectSchema
	);

	if (!controller.schema.isArray) {
		return extendedSchema;
	}

	const arraySchema = Yup.array().of(extendedSchema);
	return controller.createArrayValidationRule?.(arraySchema, initialData, translation) ?? arraySchema;
};

const createSectionValidatorsPart = (
	controller: FlexSectionController,
	initialData: unknown,
	translation: (key: string) => string
): { [key: string]: Yup.AnySchema } => {
	return isSectionEmbedded(controller.schema)
		? createSectionNestedValidators(controller, initialData, translation)
		: { [controller.schema.localizableKey]: createSectionValidatorSchema(controller, initialData, translation) };
};

export const createTypeValidationSchema = (typeModel: FlexContext, initialData: unknown, translation: (key: string) => string) => {
	const nestedEmbeddedControllers = typeModel.sectionControllers
		.filter((section) => isSectionEmbedded(section.schema))
		.flatMap(getEmbeddedSectionControllers);
	const objectSchema = Yup.object().shape(
		typeModel.sectionControllers.reduce(
			(a, section) => ({
				...a,
				...createSectionValidatorsPart(section, initialData, translation),
			}),
			{} as any
		)
	);

	return nestedEmbeddedControllers.reduce(
		(a, nestedController) => nestedController.createValidationRule?.(a, initialData, translation) ?? a,
		objectSchema
	);
};
